import { ChevronRight, Loader2, RefreshCw } from 'lucide-react'
import { motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { LiveSearchBadge } from '@/components/LiveSearchBadge'
import { SegmentedIndicator } from '@/components/SegmentedIndicator'
import { Button } from '@/components/ui/button'
import { modeLabel } from '@/lib/format'
import { useHistory } from '@/lib/history'
import type { HistoryScope } from '@/lib/history.types'
import { staggerContainer, staggerItem } from '@/lib/motion/variants'
import { useSubmitRun } from '@/lib/queries'
import type { RunSummary } from '@/lib/types'
import { useAnalytics } from '@/provider/useAuth'
import { useTrackerHref } from '@/provider/useTrackerHref'
import { useTrackerNavigate } from '@/provider/useTrackerNavigate'

const SCOPE_INDICATOR = 'history-scope-indicator'

const SCOPES: readonly [HistoryScope, string][] = [
  ['personal', 'My runs'],
  ['org', "Your team's runs"],
]

// Defaults to personal ("My runs") with an org toggle ("Your team's runs").
// Reachable only when auth is present (routed under the authed branch in
// TrackerApp); the personal/org lens maps to the backend's two query lenses.
export default function History() {
  const [scope, setScope] = useState<HistoryScope>('personal')
  const analytics = useAnalytics()
  const navigate = useTrackerNavigate()
  const rerunRun = useSubmitRun({ forceFresh: true })
  const [rerunningId, setRerunningId] = useState<string | null>(null)

  // Fires once on mount and again whenever the lens changes — `scope` is the
  // only dependency, so unrelated re-renders (e.g. the query resolving) don't
  // re-emit.
  useEffect(() => {
    analytics.track('history_viewed', { scope })
  }, [scope, analytics])
  const href = useTrackerHref()
  const { data, isPending, isError } = useHistory(scope)

  async function rerun(run: RunSummary) {
    setRerunningId(run.id)

    const targetMarketIds =
      run.effectiveTargetMarketIds ?? run.requestedTargetMarketIds
    const marketInput =
      targetMarketIds && targetMarketIds.length > 0
        ? { targetMarketIds }
        : { market: run.market ?? 'Global' }

    try {
      const result = await rerunRun.mutateAsync({
        brand: run.brand,
        category: run.category,
        ...marketInput,
        liveSearch: run.liveSearchRequested ?? run.liveSearch ?? false,
      })

      navigate(`/run/${result.runId}`)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Could not start the rerun.'
      )
    } finally {
      setRerunningId(null)
    }
  }

  return (
    <main className="px-6 pb-20 mx-auto w-full max-w-4xl">
      <section className="pt-8 pb-4 flex items-end justify-between flex-wrap gap-4">
        <h1 className="text-3xl font-semibold">History</h1>
        <div className="inline-flex rounded-lg border border-border overflow-hidden text-sm">
          {SCOPES.map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setScope(id)}
              className={`relative px-4 py-2 cursor-pointer transition-colors ${scope === id ? 'text-primary' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'}`}
            >
              {scope === id && (
                <SegmentedIndicator layoutId={SCOPE_INDICATOR} />
              )}
              <span className="relative">{label}</span>
            </button>
          ))}
        </div>
      </section>

      {isPending && (
        <p className="py-8 text-center text-muted-foreground">
          Loading history…
        </p>
      )}
      {isError && (
        <p className="py-8 text-center text-destructive">
          Could not load history.
        </p>
      )}
      {data && data.length === 0 && (
        <p className="py-8 text-center text-muted-foreground">No runs yet.</p>
      )}
      {data &&
        data.length > 0 &&
        data.some((r) => r.liveSearch) &&
        data.some((r) => !r.liveSearch) && (
          <p className="pb-4 text-xs text-muted-foreground">
            This list mixes live-search and direct runs — their scores are not
            directly comparable.
          </p>
        )}
      {data && data.length > 0 && (
        <motion.ul
          key={scope}
          variants={staggerContainer}
          initial="hidden"
          animate="shown"
          className="space-y-2"
        >
          {data.map((r) => (
            <motion.li key={r.id} variants={staggerItem}>
              <div className="flex flex-col items-stretch gap-2 rounded-xl border border-border bg-card px-3 py-2 transition-colors hover:border-ring/60 hover:bg-muted/40 sm:flex-row sm:items-center sm:gap-3">
                <Link
                  to={href(`/run/${r.id}`)}
                  onClick={() =>
                    analytics.track('run_reopened', {
                      runId: r.id,
                      scope,
                      status: r.status,
                    })
                  }
                  className="group/link flex min-w-0 flex-1 flex-col items-start justify-between gap-2 rounded-lg px-1 py-1 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 sm:flex-row sm:items-center sm:gap-4"
                >
                  <span className="flex min-w-0 items-center gap-2 font-medium">
                    {/* Every row gets a mode pill: Gap Finder rows stand out in
                      primary; everything else reads "Normal" in a neutral chip. */}
                    {r.mode === 'intent_map' ? (
                      <span className="shrink-0 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-primary">
                        {modeLabel(r.mode)}
                      </span>
                    ) : (
                      <span className="shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                        Normal
                      </span>
                    )}
                    {r.liveSearch && <LiveSearchBadge />}
                    <span className="min-w-0 truncate">
                      {r.brand}{' '}
                      <span className="text-muted-foreground font-normal">
                        in
                      </span>{' '}
                      {r.category}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                    {r.targetMarketLabel ?? r.market ?? 'Global'} · {r.status} ·{' '}
                    {new Date(r.createdAt).toLocaleDateString()}
                    <ChevronRight className="size-4 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                  </span>
                </Link>
                {r.mode === 'direct' &&
                  (r.status === 'complete' || r.status === 'error') && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto"
                      disabled={rerunRun.isPending}
                      onClick={() => void rerun(r)}
                    >
                      {rerunningId === r.id ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="size-3.5" />
                      )}
                      Run again
                    </Button>
                  )}
              </div>
            </motion.li>
          ))}
        </motion.ul>
      )}
    </main>
  )
}
